/**
 * TanStack Query hooks for Kuvio data. One hook per logical resource;
 * mutations invalidate related queries to keep the UI in sync.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient, McpError } from "../mcp-client";
import { authFetch, getCurrentWorkspaceId, patchWorkspaceName } from "../auth";
import { queryKeys, toolNames } from "../query-keys";
import type {
  AgentConfig,
  EntityRecord,
  EntityTypeDefinition,
  LibraryNode,
  LibraryNodeDetail,
  ListFilters,
  WorkflowDefinition,
  WorkspaceInfo,
} from "../types";

function defaultAgentConfig(agentName: string): AgentConfig {
  return {
    greeting: `I'm your ${agentName} agent. How can I help?`,
    model: null,
    workspace_scope: "all",
    table_scope: "all",
    max_steps: 10,
    include_child_data: true,
    starters: null,
    avatar_file_id: null,
    avatar_prompt: null,
    max_output_tokens: null,
    model_policy: null,
  };
}

// ---------------------------------------------------------------------------
// Read hooks
// ---------------------------------------------------------------------------

export function useWorkspaceInfo() {
  return useQuery({
    queryKey: queryKeys.workspace(),
    queryFn: () => mcpClient.getWorkspaceInfo(),
    staleTime: 60_000,
  });
}

export function useEntityTypes() {
  return useQuery({
    queryKey: queryKeys.entityTypes(),
    queryFn: () => mcpClient.listEntityTypes(),
    staleTime: 30_000,
  });
}

/** Single entity type — derived from the list query for cache coherence. */
export function useEntityType(slug: string | undefined) {
  const all = useEntityTypes();
  return {
    ...all,
    data: slug ? all.data?.find((t) => t.slug === slug) : undefined,
  };
}

export function useEntities(
  type: EntityTypeDefinition | undefined,
  filters?: ListFilters,
  opts?: { keepPreviousData?: boolean },
) {
  // Normalise: drop empty/undefined entries so two callers with equivalent
  // "no filters" intent share a cache slot, and keys are deterministic.
  const normalized = normalizeFilters(filters);
  return useQuery({
    queryKey: type
      ? ([...queryKeys.entities(type.slug), normalized] as const)
      : ["entities"],
    queryFn: () => {
      if (!type) throw new Error("entity type unresolved");
      return mcpClient.listEntities(toolNames(type).list, normalized);
    },
    enabled: !!type,
    staleTime: 10_000,
    // №14.1.1-D — the declared-view toolbar changes the query key on every
    // search/filter keystroke. Keeping the previous page visible (instead of
    // dropping to a skeleton, which would unmount the toolbar and steal input
    // focus) makes filtering feel instant and keeps the controls on screen.
    ...(opts?.keepPreviousData ? { placeholderData: keepPreviousData } : {}),
  });
}

function normalizeFilters(filters?: ListFilters): ListFilters | undefined {
  if (!filters) return undefined;
  const out: ListFilters = {};
  for (const [k, v] of Object.entries(filters)) {
    // Sprint 2.5: `parent: null` is a meaningful filter (root nodes only).
    // Drop empty strings and undefined; preserve explicit null for `parent`.
    if (v === undefined) continue;
    if (v === "") continue;
    if (v === null && k !== "parent") continue;
    out[k] = v;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

export function useEntity(type: EntityTypeDefinition | undefined, id: string | undefined) {
  return useQuery({
    queryKey: type && id ? queryKeys.entity(type.slug, id) : ["entity"],
    queryFn: () => {
      if (!type || !id) throw new Error("entity unresolved");
      return mcpClient.getEntity(toolNames(type).get, id);
    },
    enabled: !!type && !!id,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Library hooks (Sprint 3.1)
// ---------------------------------------------------------------------------

export function useLibraryNode(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.libraryNode(id) : ["library-node"],
    queryFn: () => {
      if (!id) throw new Error("node id unresolved");
      return mcpClient.getNode(id);
    },
    enabled: !!id,
    staleTime: 10_000,
  });
}

/** Sprint 3.2 `find_related` — all relationship layers for an entity/node id. */
export function useFindRelated(id: string | undefined) {
  return useQuery({
    queryKey: ["find-related", id],
    queryFn: () => {
      if (!id) throw new Error("id unresolved");
      return mcpClient.findRelated(id);
    },
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useListTree(workspaceId?: string) {
  return useQuery({
    queryKey: queryKeys.libraryTree(),
    // The console "tree" is the agent hierarchy — every consumer filters for
    // type === "agent". Request agents directly: an unfiltered deep scan
    // (SELECT * over document/instructions text for every node) overflows the
    // Durable Object SQLite result limit on large workspaces and silently
    // returns zero rows, which made the agent rail render empty even though
    // the workspace had agents. Filtering to agents keeps the result small
    // and complete. (Server-side: deep unfiltered list_tree still needs a
    // lightweight projection so MCP/AI clients don't hit the same limit.)
    //
    // Sprint 5u: also fetch folders — both are lightweight structural nodes, and
    // the rail / folder cockpit need folders to render the tree correctly.
    // Pages/memories/tables stay excluded (their text overflows the scan). Two
    // type-filtered calls keep each result small; consumers that want only
    // agents already filter by type.
    queryFn: async () => {
      const [agents, folders] = await Promise.all([
        mcpClient.listTree({ depth: -1, type: "agent" }),
        mcpClient.listTree({ depth: -1, type: "folder" }),
      ]);
      return [...agents, ...folders];
    },
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

/** Sprint 5u — all table nodes (lightweight, type-filtered) for entity-ref resolution. */
export function useTableNodes() {
  return useQuery({
    queryKey: ["table-nodes"],
    queryFn: () => mcpClient.listTree({ type: "table", depth: -1 }),
    staleTime: 30_000,
  });
}

export function useLibraryChildren(parentId: string | null) {
  return useQuery({
    queryKey: queryKeys.libraryChildren(parentId),
    queryFn: () => mcpClient.listTree({ parent_id: parentId ?? undefined, depth: 2 }),
    staleTime: 15_000,
  });
}

export function useListChildren(parentId: string | undefined) {
  return useQuery({
    queryKey: parentId ? queryKeys.listChildren(parentId) : ["list-children"],
    queryFn: () => {
      if (!parentId) return [];
      return mcpClient.listChildren(parentId);
    },
    enabled: !!parentId,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// View hooks (Sprint 5.1)
// ---------------------------------------------------------------------------

export function useViews(entityType?: string, viewType?: string) {
  return useQuery({
    queryKey: queryKeys.views(entityType, viewType),
    queryFn: () => mcpClient.listViews({ entity_type: entityType, view_type: viewType }),
    staleTime: 30_000,
  });
}

export function useView(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.view(id) : ["view"],
    queryFn: () => {
      if (!id) throw new Error("view id unresolved");
      return mcpClient.getView(id);
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { name: string; description?: string; parent_id?: string }) =>
      mcpClient.createFolder(params),
    onSuccess: (_node, vars) => {
      if (vars.parent_id) {
        qc.invalidateQueries({ queryKey: queryKeys.listChildren(vars.parent_id) });
        qc.invalidateQueries({ queryKey: queryKeys.libraryNode(vars.parent_id) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.libraryChildren(vars.parent_id ?? null) });
      qc.invalidateQueries({ queryKey: ["child-nodes"] });
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}

export function usePromoteToAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      nodeId: string;
      restore_from_reserve?: boolean;
      instructions?: string;
      agent_config?: Record<string, unknown>;
    }) =>
      mcpClient.promoteToAgent(vars.nodeId, {
        restore_from_reserve: vars.restore_from_reserve,
        instructions: vars.instructions,
        agent_config: vars.agent_config,
      }),
    onSuccess: (node) => {
      qc.invalidateQueries({ queryKey: queryKeys.libraryNode(node.id) });
      qc.invalidateQueries({ queryKey: queryKeys.libraryChildren(node.parent_id) });
      if (node.parent_id) qc.invalidateQueries({ queryKey: queryKeys.listChildren(node.parent_id) });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}

export function useDemoteToFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { nodeId: string; archive_children?: boolean }) =>
      mcpClient.demoteToFolder(vars.nodeId, { archive_children: vars.archive_children }),
    onSuccess: (node) => {
      qc.invalidateQueries({ queryKey: queryKeys.libraryNode(node.id) });
      qc.invalidateQueries({ queryKey: queryKeys.libraryChildren(node.parent_id) });
      if (node.parent_id) qc.invalidateQueries({ queryKey: queryKeys.listChildren(node.parent_id) });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}

export function useCreateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { name: string; parent_id?: string; instructions?: string; document?: string }) => {
      const node = await mcpClient.createTopic(params);
      await mcpClient.updateNode({
        id: node.id,
        agent_config: defaultAgentConfig(params.name),
      });
      return node;
    },
    onSuccess: (_node, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.libraryChildren(vars.parent_id ?? null) });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
      if (vars.parent_id) qc.invalidateQueries({ queryKey: queryKeys.libraryNode(vars.parent_id) });
    },
  });
}

/** Sprint 5o — every page in the workspace (subtree-wide), for the workspace cockpit. */
export function useWorkspacePages(enabled = true) {
  return useQuery({
    queryKey: queryKeys.workspacePages(),
    queryFn: () => mcpClient.listTree({ depth: -1, type: "page" }),
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** Invalidate every query that could include a page (panels, rail, cockpit). */
function invalidatePageQueries(qc: ReturnType<typeof useQueryClient>, parentId?: string | null) {
  qc.invalidateQueries({ queryKey: queryKeys.workspacePages() });
  qc.invalidateQueries({ queryKey: ["child-nodes"] });
  qc.invalidateQueries({ queryKey: queryKeys.workspace() });
  if (parentId) {
    qc.invalidateQueries({ queryKey: queryKeys.libraryChildren(parentId) });
    qc.invalidateQueries({ queryKey: queryKeys.libraryNode(parentId) });
  }
}

export function useCreatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      name: string;
      parent_id?: string;
      body?: string;
      surface?: string;
      status?: string;
      frontmatter?: Record<string, unknown>;
    }) => mcpClient.createPage(params),
    onSuccess: (_node, vars) => invalidatePageQueries(qc, vars.parent_id ?? null),
  });
}

export function useUpdatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: Parameters<typeof mcpClient.updatePage>[0]) =>
      mcpClient.updatePage(params),
    onSuccess: (node) => {
      qc.invalidateQueries({ queryKey: queryKeys.libraryNode(node.id) });
      invalidatePageQueries(qc, node.parent_id);
    },
  });
}

export function useUpdateNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: Parameters<typeof mcpClient.updateNode>[0]) =>
      mcpClient.updateNode(params),
    onSuccess: (node) => {
      qc.invalidateQueries({ queryKey: queryKeys.libraryNode(node.id) });
      qc.invalidateQueries({ queryKey: queryKeys.libraryChildren(node.parent_id) });
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}

export function useUpdateWorkspaceName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => mcpClient.updateWorkspaceName(name),
    onSuccess: (_data, name) => {
      const wsId = getCurrentWorkspaceId();
      if (wsId) patchWorkspaceName(wsId, name);
      const ws = qc.getQueryData<WorkspaceInfo>(queryKeys.workspace());
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      if (ws?.root_node_id) {
        qc.invalidateQueries({ queryKey: queryKeys.libraryNode(ws.root_node_id) });
      }
    },
  });
}

export function useSetWorkspaceInstructions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instructions: string) =>
      mcpClient.setWorkspaceInstructions(instructions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
      const ws = qc.getQueryData<WorkspaceInfo>(queryKeys.workspace());
      if (ws?.root_node_id) {
        qc.invalidateQueries({ queryKey: queryKeys.libraryNode(ws.root_node_id) });
      }
    },
  });
}

export function useArchiveNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg: string | { id: string; cascade?: boolean }) => {
      const { id, cascade } = typeof arg === "string" ? { id: arg, cascade: undefined } : arg;
      return mcpClient.archiveNodeCall(id, cascade);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["library-children"] });
      qc.invalidateQueries({ queryKey: ["child-nodes"] });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
    },
  });
}

export function useMoveNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; new_parent_id: string | null; sort_order?: number }) =>
      mcpClient.moveNode(input),
    onSuccess: (_node, vars) => {
      qc.invalidateQueries({ queryKey: ["library-children"] });
      qc.invalidateQueries({ queryKey: ["child-nodes"] });
      qc.invalidateQueries({ queryKey: queryKeys.libraryNode(vars.id) });
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}

export function usePromoteRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entityId: string) => mcpClient.promoteRow(entityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
      qc.invalidateQueries({ queryKey: ["library-children"] });
    },
  });
}

export function useDemoteRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entityId: string) => mcpClient.demoteRow(entityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["library-children"] });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateEntity(type: EntityTypeDefinition | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: Record<string, unknown>) => {
      if (!type) throw new Error("entity type unresolved");
      return mcpClient.createEntity(toolNames(type).create, fields);
    },
    onSuccess: (entity) => {
      if (!type) return;
      qc.invalidateQueries({ queryKey: queryKeys.entities(type.slug) });
      qc.setQueryData(queryKeys.entity(type.slug, entity.id), entity);
    },
  });
}

export function useUpdateEntity(type: EntityTypeDefinition | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      fields,
      expectedVersion,
    }: {
      id: string;
      fields: Record<string, unknown>;
      expectedVersion?: number;
    }) => {
      if (!type) throw new Error("entity type unresolved");
      return mcpClient.updateEntity(toolNames(type).update, id, fields, expectedVersion);
    },
    onSuccess: (entity) => {
      if (!type) return;
      qc.invalidateQueries({ queryKey: queryKeys.entities(type.slug) });
      qc.setQueryData(queryKeys.entity(type.slug, entity.id), entity);
      // Sprint 12: a field edit / stage transition changes the row-detail
      // payload (fields, _transitions, resolved forward refs) — refresh it.
      qc.invalidateQueries({ queryKey: queryKeys.rowDetail(type.slug, entity.id) });
    },
  });
}

export function useArchiveEntity(type: EntityTypeDefinition | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expectedVersion }: { id: string; expectedVersion?: number }) => {
      if (!type) throw new Error("entity type unresolved");
      return mcpClient.archiveEntity(toolNames(type).archive, id, expectedVersion);
    },
    onSuccess: (result, vars) => {
      if (!type) return;
      // Wide invalidation: any entity in the workspace might have been
      // touched by cascade-archive or nullify side-effects, so drop the
      // full entities/<slug> cache plus any individual cached descendants
      // listed in cascade.archived.
      qc.invalidateQueries({ queryKey: queryKeys.entities(type.slug) });
      qc.removeQueries({ queryKey: queryKeys.entity(type.slug, vars.id) });
      if (result.cascade?.archived) {
        for (const step of result.cascade.archived) {
          qc.invalidateQueries({ queryKey: queryKeys.entities(step.type) });
          qc.removeQueries({ queryKey: queryKeys.entity(step.type, step.id) });
        }
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Schema mutations — create / update entity type definitions. The server
// returns the new definition; we invalidate the entity_types cache so the
// sidebar and Schema browser re-render with the new shape.
// ---------------------------------------------------------------------------

export function useCreateEntityType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      definition: Omit<EntityTypeDefinition, "_version">,
    ): Promise<EntityTypeDefinition> => {
      const result = await mcpClient.toolsCall<EntityTypeDefinition>(
        "create_entity_type",
        definition as unknown as Record<string, unknown>,
      );
      if (result.isError) throw toEntityTypeError(result);
      return result.structuredContent as EntityTypeDefinition;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.entityTypes() });
    },
  });
}

/**
 * Sprint 2.7 admin tools — workspace status and hard-delete an entity type.
 * `delete_entity_type` is irreversible — the caller must show a confirmation
 * before invoking. `admin_workspace_status` returns per-type entity counts
 * (active/archived) that the UI uses for the impact preview.
 */

export interface WorkspaceStatusEntityType {
  slug: string;
  name: string;
  id_prefix: string;
  module: string;
  hidden: boolean;
  version: number;
  field_count: number;
  entities: { total: number; active: number; archived: number };
  has_workflow: boolean;
  has_views: boolean;
  has_instructions: boolean;
}

export interface WorkspaceStatus {
  entity_types: WorkspaceStatusEntityType[];
  totals: {
    entity_types: number;
    entity_types_hidden: number;
    entities: number;
    entities_archived: number;
    relationships: number;
    files: number;
    files_size_bytes: number;
    context_entries: number;
  };
}

export function useWorkspaceStatus() {
  return useQuery({
    queryKey: ["admin", "workspace-status"] as const,
    queryFn: async (): Promise<WorkspaceStatus> => {
      const result = await mcpClient.toolsCall<WorkspaceStatus>(
        "admin_workspace_status",
        {},
      );
      if (result.isError) throw new Error("workspace_status_failed");
      return result.structuredContent as WorkspaceStatus;
    },
    staleTime: 10_000,
  });
}

export interface DeleteEntityTypeSummary {
  ok: true;
  slug: string;
  entities_deleted: number;
  relationships_deleted: number;
  audit_entries_deleted: number;
  files_deleted: number;
}

export function useDeleteEntityType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string): Promise<DeleteEntityTypeSummary> => {
      const result = await mcpClient.toolsCall<DeleteEntityTypeSummary>(
        "delete_entity_type",
        { slug, confirm: true },
      );
      if (result.isError) {
        const data = (result.structuredContent ?? {}) as { error?: string };
        throw new Error(typeof data.error === "string" ? data.error : "delete_failed");
      }
      return result.structuredContent as DeleteEntityTypeSummary;
    },
    onSuccess: (result) => {
      // The type and all its data are gone — drop the cached entity_types list
      // and any cached entity/list queries for that slug.
      qc.invalidateQueries({ queryKey: queryKeys.entityTypes() });
      qc.removeQueries({ queryKey: queryKeys.entities(result.slug) });
      qc.invalidateQueries({ queryKey: ["admin", "workspace-status"] });
    },
  });
}

export function useUpdateEntityType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      slug: string;
      // `workflow: null` clears the workflow (the engine treats explicit null as
      // "delete this key") — Sprint 14b's delete-workflow flow relies on it.
      patch: Omit<Partial<EntityTypeDefinition>, "workflow"> & {
        workflow?: WorkflowDefinition | null;
      };
      expectedVersion?: number;
    }): Promise<EntityTypeDefinition> => {
      const args: Record<string, unknown> = {
        slug: input.slug,
        ...input.patch,
      };
      if (input.expectedVersion !== undefined) {
        args.expected_version = input.expectedVersion;
      }
      const result = await mcpClient.toolsCall<EntityTypeDefinition>(
        "update_entity_type",
        args,
      );
      if (result.isError) throw toEntityTypeError(result);
      return result.structuredContent as EntityTypeDefinition;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.entityTypes() });
    },
  });
}

function toEntityTypeError(result: {
  structuredContent?: unknown;
}): Error {
  const data = (result.structuredContent ?? {}) as {
    error?: string;
    errors?: { field: string; code: string; message: string }[];
    current_version?: number;
    expected_version?: number;
  };
  if (data.error === "validation_failed") {
    const err = new Error("Validation failed") as Error & {
      kind: "validation";
      fieldErrors: typeof data.errors;
    };
    err.kind = "validation";
    err.fieldErrors = data.errors ?? [];
    return err;
  }
  if (data.error === "version_conflict") {
    return new Error(
      `Version conflict — current is v${data.current_version}, you sent v${data.expected_version}`,
    );
  }
  return new Error(typeof data.error === "string" ? data.error : "Update failed");
}

// ---------------------------------------------------------------------------
// Sprint 3.4 — Agent run hooks
// (Sprint 14 removed the agent-messaging "Inbox" hooks.)
// ---------------------------------------------------------------------------

function agentApiBase(): string {
  const env = import.meta.env as { VITE_MCP_ENDPOINT?: string };
  const mcp = env.VITE_MCP_ENDPOINT ?? "https://dev.kuvio.ai/mcp";
  return mcp.replace(/\/mcp\/?$/, "");
}

export interface ActiveAgentRun {
  id: string;
  node_id: string;
  name: string;
  trigger: string;
  started_at: string;
  model: string;
}

export function useActiveAgentRuns(enabled = true) {
  return useQuery({
    queryKey: ["active-agent-runs"],
    queryFn: async () => {
      const base = agentApiBase();
      const resp = await authFetch(`${base}/ai/agents/active`);
      if (!resp.ok) return [];
      const data = (await resp.json()) as { active_runs: ActiveAgentRun[] };
      return data.active_runs ?? [];
    },
    refetchInterval: enabled ? 3000 : false,
    staleTime: 2000,
  });
}

export function useAgentHierarchy() {
  return useQuery({
    queryKey: ["agent-hierarchy"],
    queryFn: async () => {
      const result = await mcpClient.toolsCall<{ nodes: unknown[] }>(
        "list_tree",
        { depth: 10 },
      );
      const nodes = (result.structuredContent as { nodes?: Array<Record<string, unknown>> })?.nodes ?? [];
      return nodes.filter(n => n.agent_config != null || n.type === "agent");
    },
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Re-export McpError so callers don't need a second import
// ---------------------------------------------------------------------------

export type { EntityRecord, EntityTypeDefinition, LibraryNode, LibraryNodeDetail };
export { McpError };
